// Lead management API service
import apiClient from './api'
import {
  Lead,
  LeadCreate,
  LeadUpdate,
  LeadListResponse,
  LeadFilters,
  BulkImportRequest,
  BulkImportResult,
  LeadSegment,
  SegmentCreate,
  SegmentUpdate,
} from '../types/lead'

export const leadService = {
  // Get all leads with filtering, pagination, and sorting
  getAll: async (params: {
    page?: number
    per_page?: number
    gst_verification_status?: string[]
    location?: string
    city?: string
    state?: string
    source?: string[]
    interest_category?: string
    status?: string[]
    campaign_tag?: string
    date_from?: string
    date_to?: string
    search?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  } = {}): Promise<LeadListResponse> => {
    const response = await apiClient.get<LeadListResponse>('/admin/leads/', {
      params,
    })
    return response.data
  },

  // Get single lead by ID
  getById: async (leadId: string): Promise<Lead> => {
    const response = await apiClient.get<Lead>(`/admin/leads/${leadId}`)
    return response.data
  },

  // Create new lead
  create: async (data: LeadCreate): Promise<Lead> => {
    const response = await apiClient.post<Lead>('/admin/leads/', data)
    return response.data
  },

  // Update existing lead
  update: async (leadId: string, data: LeadUpdate): Promise<Lead> => {
    const response = await apiClient.put<Lead>(`/admin/leads/${leadId}`, data)
    return response.data
  },

  // Soft delete lead
  delete: async (leadId: string): Promise<void> => {
    await apiClient.delete(`/admin/leads/${leadId}`)
  },

  // Bulk import leads from CSV or Excel
  bulkImport: async (data: BulkImportRequest): Promise<BulkImportResult> => {
    const response = await apiClient.post<BulkImportResult>('/admin/leads/bulk-import', data)
    return response.data
  },

  // Export leads to CSV
  export: async (filters?: LeadFilters): Promise<Blob> => {
    const response = await apiClient.get('/admin/leads/export/csv', {
      params: filters,
      responseType: 'blob',
    })
    return response.data
  },

  // Get all saved segments
  getSegments: async (): Promise<LeadSegment[]> => {
    const response = await apiClient.get<LeadSegment[]>('/admin/leads/segments')
    return response.data
  },

  // Create new segment
  createSegment: async (data: SegmentCreate): Promise<LeadSegment> => {
    const response = await apiClient.post<LeadSegment>('/admin/leads/segments', data)
    return response.data
  },

  // Get single segment by ID
  getSegmentById: async (segmentId: string): Promise<LeadSegment> => {
    const response = await apiClient.get<LeadSegment>(`/admin/leads/segments/${segmentId}`)
    return response.data
  },

  // Update existing segment
  updateSegment: async (segmentId: string, data: SegmentUpdate): Promise<LeadSegment> => {
    const response = await apiClient.put<LeadSegment>(`/admin/leads/segments/${segmentId}`, data)
    return response.data
  },

  // Delete segment
  deleteSegment: async (segmentId: string): Promise<void> => {
    await apiClient.delete(`/admin/leads/segments/${segmentId}`)
  },

  // Get real-time lead count for a segment
  getSegmentCount: async (segmentId: string): Promise<{ segment_id: string; segment_name: string; lead_count: number }> => {
    const response = await apiClient.get(`/admin/leads/segments/${segmentId}/count`)
    return response.data
  },

  // Get active WhatsApp templates for the segment follow-up picker (scoped to leads.write,
  // unlike the full notification-templates endpoint which requires notifications.templates)
  getWhatsAppFollowupTemplates: async (): Promise<Array<{ id: string; name: string }>> => {
    const response = await apiClient.get('/admin/leads/segments/whatsapp-templates')
    return response.data
  },

  // Send a WhatsApp follow-up campaign to every lead currently matching a segment
  sendSegmentWhatsAppFollowup: async (
    segmentId: string,
    data: { template_id: string; campaign_name?: string }
  ): Promise<{
    success: boolean
    campaign_id: string
    campaign_name: string
    segment_lead_count: number
    recipients_added: number
    opted_out: number
    message: string
  }> => {
    const response = await apiClient.post(`/admin/leads/segments/${segmentId}/whatsapp-followup`, data)
    return response.data
  },
}
